import React from 'react';
import { Link } from 'react-router-dom';
import { drumLessonroom, drumLessonroom2, drumPractice, drumRoby, drumLaunge, drumTeacher } from '../assets';
import { useLanguage } from '../contexts/LanguageContext';
import { useScrollToTop } from '../hooks/useScrollToTop';

const Home: React.FC = () => {
  const { t } = useLanguage();

  useScrollToTop();

  return (
    <div className="home-page">
      {/* Hero Section */}
      <section className="hero-section" id="home">
        <img src={drumLessonroom} alt="Dream Drums" className="hero-bg-image" />
        <div className="hero-overlay"></div>
        <div className="hero-content">
          <div className="hero-eyebrow">
            <span className="hero-badge">{t('home_hero_badge_adult')}</span>
            DRUM STUDIO · 1:1 LESSON
          </div>
          <h1 className="hero-title">
            {t('home_hero_title_1')}<br />
            {t('home_hero_title_2')}
          </h1>
          <p className="hero-tagline">{t('home_hero_tagline')}</p>
          <div className="hero-buttons">
            <Link to="/lesson-request?type=일반" className="btn-hero-primary">
              {t('lesson_request')}
            </Link>
            <Link to="/one-day-class?type=일반" className="btn-hero-outline">
              {t('oneday_apply')}
            </Link>
          </div>
        </div>
      </section>

      <div className="home-main">
        {/* Programs Section */}
        <section className="programs-section" id="programs">
          <div className="programs-grid">
            <div className="program-card">
              <div className="program-title">{t('home_program_regular_title')}</div>
              <p className="program-desc">{t('home_program_regular_desc')}</p>
              <div className="program-price">{t('home_program_regular_price')}</div>
            </div>
            <div className="program-card">
              <div className="program-title">{t('home_program_oneday_title')}</div>
              <p className="program-desc">{t('home_program_oneday_desc')}</p>
              <div className="program-price">{t('home_program_oneday_price')}</div>
            </div>
            <div className="program-card">
              <div className="program-title">{t('home_program_practice_title')}</div>
              <p className="program-desc">{t('home_program_practice_desc')}</p>
              <div className="program-price">{t('home_program_practice_price')}</div>
            </div>
          </div>
        </section>

        {/* Facility Gallery Section */}
        <section className="facility-section" id="facility">
          <div className="section-heading">
            <h2>{t('facility_tour')}</h2>
            <span className="heading-sub">{t('home_facility_subtitle')}</span>
          </div>
          <div className="facility-grid">
            <div className="facility-item main">
              <img src={drumLessonroom2} alt={t('home_gallery_lessonroom')} />
              <div className="facility-tag">{t('home_gallery_lessonroom')}</div>
            </div>
            <div className="facility-item">
              <img src={drumLessonroom} alt={t('home_gallery_mainkit')} />
              <div className="facility-tag">{t('home_gallery_mainkit')}</div>
            </div>
            <div className="facility-item">
              <img src={drumPractice} alt={t('home_gallery_practice')} />
              <div className="facility-tag">{t('home_gallery_practice')}</div>
            </div>
            <div className="facility-item">
              <img src={drumRoby} alt={t('home_gallery_lobby')} />
              <div className="facility-tag">{t('home_gallery_lobby')}</div>
            </div>
            <div className="facility-item">
              <img src={drumLaunge} alt={t('home_gallery_lounge')} />
              <div className="facility-tag">{t('home_gallery_lounge')}</div>
            </div>
          </div>
        </section>

        {/* Teacher Section */}
        <section className="teacher-section" id="teacher">
          <div className="teacher-photo">
            <img src={drumTeacher} alt="Teacher" />
          </div>
          <div className="teacher-info">
            <div className="teacher-eyebrow">TEACHER</div>
            <h2 className="teacher-name">{t('instructor_greeting')}</h2>
            <p className="teacher-desc">{t('instructor_intro_text_1')}</p>
            <div className="teacher-chips">
              <span>{t('home_teacher_chip_curriculum')}</span>
              <span>{t('home_teacher_chip_log')}</span>
              <span>{t('home_teacher_chip_practice')}</span>
            </div>
            <Link to="/instructors" className="teacher-link">
              {t('home_view_instructor')} →
            </Link>
          </div>
        </section>

        {/* Location Section */}
        <section className="location-section" id="location">
          <div className="section-heading">
            <h2>{t('location_title')}</h2>
            <span className="heading-sub">{t('address')}</span>
          </div>
          <div className="location-grid">
            <div className="location-map">
              <iframe
                title="DreamDrums 위치"
                src="https://www.google.com/maps/embed?origin=mfe&pb=!1m3!2m1!1z6rK96riw64-EIOqzoOyWkeyLnCDsnbzsgrDrj5nqtawg7IKw65GQ66GcMTc167KI6ri4IDYtMg!6i17"
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
              <a
                href="https://map.naver.com/p/search/경기도%20고양시%20일산동구%20산두로175번길%206-2"
                target="_blank"
                rel="noopener noreferrer"
                className="map-cta"
              >
                {t('view_on_naver_map')}
              </a>
            </div>
            <div className="location-info-panel">
              <div className="location-info-grid">
                <div className="info-label">{t('address_label')}</div>
                <div className="info-value">{t('address')}</div>
                <div className="info-label">{t('footer_contact')}</div>
                <div className="info-value">{t('phone_number')} · {t('footer_email')}</div>
                <div className="info-label">{t('home_location_lesson_label')}</div>
                <div className="info-value">{t('weekdays')} 10:00–22:00 · {t('weekend')} 09:00–18:00</div>
                <div className="info-label">{t('home_location_practice_label')}</div>
                <div className="info-value">{t('home_location_practice_value')}</div>
                <div className="info-label">{t('home_location_social_label')}</div>
                <div className="info-value">
                  <a href="https://www.instagram.com/_dreamdrums/" target="_blank" rel="noopener noreferrer">{t('instagram')}</a>
                  {' · '}
                  <a href="https://blog.naver.com/dreamdrums" target="_blank" rel="noopener noreferrer">{t('naver_blog')}</a>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Band */}
        <section className="cta-band">
          <div className="cta-title">{t('home_cta_title')}</div>
          <div className="cta-sub">{t('home_cta_subtitle')}</div>
          <div className="cta-buttons">
            <Link to="/contact" className="btn-cta-white">{t('footer_contact_us')}</Link>
            <Link to="/lesson-request?type=일반" className="btn-cta-blue">{t('lesson_request')}</Link>
          </div>
        </section>
      </div>
    </div>
  );
};

export default Home;
